import React, { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { WeeklySplit, WeeklySplitDay, MesocycleWeek, WeekIntent } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Calendar, Settings, Plus, Trash2, Check, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { ForgePill, type ForgeColor } from '@/components/ui/ForgeUI';

interface SplitManagerProps {
  onSplitChange: () => void;
  triggerButton?: React.ReactNode;
}

const DEFAULT_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const CATEGORIES = ['Push', 'Pull', 'Legs', 'Upper', 'Lower', 'Full Body', 'Core/Cardio', 'Rest', 'Other', 'Custom...'];

const WEEK_INTENTS: { value: WeekIntent; label: string; color: string; description: string }[] = [
  { value: 'accumulation', label: 'Accumulation', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30', description: 'Build volume progressively' },
  { value: 'overreach', label: 'Overreach', color: 'text-orange-400 bg-orange-400/10 border-orange-400/30', description: 'Peak intensity push' },
  { value: 'deload', label: 'Deload', color: 'text-blue-400 bg-blue-400/10 border-blue-400/30', description: 'Reduce load ~40-60%' },
  { value: 'maintenance', label: 'Maintenance', color: 'text-purple-400 bg-purple-400/10 border-purple-400/30', description: 'Hold current level' },
];

/**
 * Single source of truth for muscle group → outlined pill badge colors.
 * Matches getWorkoutTypeTint in WorkoutCalendar.tsx exactly.
 */
function getSplitCategoryPill(category: string): { border: string; text: string } {
  const c = category.toLowerCase();
  if (c.includes('leg') || c.includes('squat') || c.includes('deadlift') || c.includes('glute') || c.includes('quad') || c.includes('hamstring'))
    return { border: 'border-red-400/70', text: 'text-red-300' };
  if (c.includes('push') || c.includes('chest') || c.includes('tricep') || c.includes('delt') || c.includes('military'))
    return { border: 'border-orange-400/70', text: 'text-orange-300' };
  if (c.includes('pull') || c.includes('back') || c.includes('bicep') || c.includes('row'))
    return { border: 'border-blue-400/70', text: 'text-blue-300' };
  if (c.includes('upper'))
    return { border: 'border-yellow-400/70', text: 'text-yellow-300' };
  if (c.includes('lower'))
    return { border: 'border-purple-400/70', text: 'text-purple-300' };
  if (c.includes('full') || c.includes('total'))
    return { border: 'border-green-400/70', text: 'text-green-300' };
  if (c.includes('core') || c.includes('cardio'))
    return { border: 'border-pink-400/70', text: 'text-pink-300' };
  if (c.includes('arm') || c.includes('curl') || c.includes('shoulder'))
    return { border: 'border-teal-400/70', text: 'text-teal-300' };
  if (c === 'rest')
    return { border: 'border-slate-500/50', text: 'text-slate-400' };
  // Other / Custom
  return { border: 'border-slate-400/50', text: 'text-slate-300' };
}

/** Get the accent border color (CSS value) for a split's left accent — uses first non-Rest day */
function getSplitAccentColor(days: WeeklySplitDay[]): string {
  const firstActive = days.find((d) => d.category.toLowerCase() !== 'rest');
  if (!firstActive) return 'rgb(100 116 139 / 0.5)'; // slate fallback
  const c = firstActive.category.toLowerCase();
  if (c.includes('leg') || c.includes('squat') || c.includes('deadlift') || c.includes('glute') || c.includes('quad') || c.includes('hamstring'))
    return 'rgb(248 113 113 / 0.8)'; // red-400
  if (c.includes('push') || c.includes('chest') || c.includes('tricep') || c.includes('delt') || c.includes('military'))
    return 'rgb(251 146 60 / 0.8)'; // orange-400
  if (c.includes('pull') || c.includes('back') || c.includes('bicep') || c.includes('row'))
    return 'rgb(96 165 250 / 0.8)'; // blue-400
  if (c.includes('upper'))
    return 'rgb(250 204 21 / 0.8)'; // yellow-400
  if (c.includes('lower'))
    return 'rgb(167 139 250 / 0.8)'; // purple-400
  if (c.includes('full') || c.includes('total'))
    return 'rgb(74 222 128 / 0.8)'; // green-400
  if (c.includes('core') || c.includes('cardio'))
    return 'rgb(244 114 182 / 0.8)'; // pink-400
  if (c.includes('arm') || c.includes('curl') || c.includes('shoulder'))
    return 'rgb(45 212 191 / 0.8)'; // teal-400
  return 'rgb(148 163 184 / 0.6)'; // slate-400 fallback
}

/** Map split category string to ForgeColor key (single source of truth via ForgeUI) */
function getSplitForgeColor(category: string): ForgeColor {
  const c = category.toLowerCase();
  if (c.includes('leg') || c.includes('squat') || c.includes('deadlift') || c.includes('glute') || c.includes('quad') || c.includes('hamstring')) return 'legs';
  if (c.includes('push') || c.includes('chest') || c.includes('tricep') || c.includes('delt') || c.includes('military')) return 'chest';
  if (c.includes('pull') || c.includes('back') || c.includes('bicep') || c.includes('row')) return 'pull';
  if (c.includes('upper')) return 'orange';
  if (c.includes('lower')) return 'blue';
  if (c.includes('full') || c.includes('total')) return 'green';
  if (c.includes('core') || c.includes('cardio')) return 'core';
  if (c.includes('arm') || c.includes('curl') || c.includes('shoulder')) return 'arms-shoulders';
  if (c === 'rest') return 'rest';
  return 'grey';
}

/** Outlined pill badge — delegates to ForgeUI ForgePill */
function SplitPill({ category }: { category: string }) {
  return <ForgePill color={getSplitForgeColor(category)} label={category} size="sm" />;
}

function makeDefaultWeekDays(): WeeklySplitDay[] {
  return DEFAULT_DAYS.map((name, idx) => ({ dayIdx: idx, name, category: 'Rest' }));
}

function makeDefaultMesocycleWeeks(): MesocycleWeek[] {
  return [
    { weekNumber: 1, intent: 'accumulation', days: makeDefaultWeekDays() },
    { weekNumber: 2, intent: 'accumulation', days: makeDefaultWeekDays() },
    { weekNumber: 3, intent: 'overreach', days: makeDefaultWeekDays() },
    { weekNumber: 4, intent: 'deload', days: makeDefaultWeekDays() },
  ];
}

function getIntentStyle(intent: WeekIntent) {
  return WEEK_INTENTS.find((w) => w.value === intent) ?? WEEK_INTENTS[0];
}

function MesocycleWeekEditor({
  week, isExpanded, onToggle, onIntentChange, onDayCategoryChange, onDrop,
}: {
  week: MesocycleWeek;
  isExpanded: boolean;
  onToggle: () => void;
  onIntentChange: (intent: WeekIntent) => void;
  onDayCategoryChange: (dayIdx: number, category: string) => void;
  onDrop?: (fromWeek: number, fromDay: number, toWeek: number, toDay: number) => void;
}) {
  const intentStyle = getIntentStyle(week.intent);
  return (
    <div className="border border-border/40 rounded-sm overflow-hidden">
      <div className="flex items-center justify-between p-2.5 bg-secondary/20 cursor-pointer hover:bg-secondary/30 transition-colors" onClick={onToggle}>
        <div className="flex items-center gap-2">
          {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="font-mono text-xs font-bold text-foreground">WEEK {week.weekNumber}</span>
          <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm border uppercase tracking-wider ${intentStyle.color}`}>{intentStyle.label}</span>
        </div>
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Select value={week.intent} onValueChange={(v) => onIntentChange(v as WeekIntent)}>
            <SelectTrigger className="w-32 bg-secondary border-border font-mono text-[10px] h-7">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border border-border font-mono text-xs">
              {WEEK_INTENTS.map((wi) => (
                <SelectItem key={wi.value} value={wi.value} className="text-xs">
                  <div><div className="font-bold">{wi.label}</div><div className="text-[9px] text-muted-foreground">{wi.description}</div></div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {isExpanded && (
        <div className="p-2 space-y-1.5 bg-background/30">
          {week.days.map((day) => {
            const isPreset = CATEGORIES.filter((c) => c !== 'Custom...').includes(day.category);
            return (
              <div
                key={day.dayIdx}
                className="flex items-center gap-2 rounded-sm transition-colors"
                draggable
                onDragStart={(e) => e.dataTransfer.setData('text/plain', JSON.stringify({ fromWeek: week.weekNumber, fromDay: day.dayIdx }))}
                onDragOver={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).classList.add('bg-primary/10'); }}
                onDragLeave={(e) => { (e.currentTarget as HTMLElement).classList.remove('bg-primary/10'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  (e.currentTarget as HTMLElement).classList.remove('bg-primary/10');
                  try {
                    const { fromWeek, fromDay } = JSON.parse(e.dataTransfer.getData('text/plain'));
                    onDrop?.(fromWeek, fromDay, week.weekNumber, day.dayIdx);
                  } catch {}
                }}
              >
                <span className="font-mono text-[10px] text-muted-foreground w-7 shrink-0 cursor-grab active:cursor-grabbing select-none">{day.name.slice(0, 3)}</span>
                <Select value={isPreset ? day.category : 'Custom...'} onValueChange={(val) => onDayCategoryChange(day.dayIdx, val === 'Custom...' ? '' : val)}>
                  <SelectTrigger className="flex-1 bg-secondary border-border font-mono text-[10px] h-7"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-card border border-border font-mono text-xs">
                    {CATEGORIES.map((cat) => <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(!isPreset || day.category === '') && (
                  <Input value={day.category} onChange={(e) => onDayCategoryChange(day.dayIdx, e.target.value)} placeholder="Custom label" className="flex-1 bg-background border-border font-mono text-[10px] h-7" maxLength={15} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SplitManager({ onSplitChange, triggerButton }: SplitManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingSplit, setEditingSplit] = useState<WeeklySplit | null>(null);
  const [splitName, setSplitName] = useState('');
  const [splitDays, setSplitDays] = useState<WeeklySplitDay[]>([]);
  const [isMesocycle, setIsMesocycle] = useState(false);
  const [mesocycleWeeks, setMesocycleWeeks] = useState<MesocycleWeek[]>(makeDefaultMesocycleWeeks());
  const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set([1]));

  const utils = trpc.useUtils();
  const { data: rawSplits = [] } = trpc.splits.list.useQuery(undefined, { enabled: isOpen });

  const splits: WeeklySplit[] = rawSplits.map((s) => ({
    id: s.id, name: s.name, isActive: s.isActive,
    isMesocycle: !!(s as any).isMesocycle,
    weeks: (s as any).weeks as MesocycleWeek[] | undefined,
    days: (s.days as any[]).map((d: any) => ({ dayIdx: d.dayIdx, name: d.name, category: d.category })),
  }));

  const saveSplitMutation = trpc.splits.save.useMutation({ onSuccess: () => { utils.splits.list.invalidate(); onSplitChange(); } });
  const deleteSplitMutation = trpc.splits.delete.useMutation({ onSuccess: () => { utils.splits.list.invalidate(); onSplitChange(); } });

  const handleSelectSplit = async (id: string) => {
    const split = splits.find((s) => s.id === id);
    if (split) { await saveSplitMutation.mutateAsync({ ...split, isActive: true }); toast.success(`Active split changed to "${split.name}"`); }
  };

  const handleCreateNewSplit = () => {
    const newSplit: WeeklySplit = { id: `split-${Date.now()}`, name: 'New Custom Split', isActive: false, days: makeDefaultWeekDays() };
    setEditingSplit(newSplit); setSplitName(newSplit.name); setSplitDays(newSplit.days);
    setIsMesocycle(false); setMesocycleWeeks(makeDefaultMesocycleWeeks()); setExpandedWeeks(new Set([1]));
  };

  const handleEditSplit = (split: WeeklySplit) => {
    setEditingSplit(split); setSplitName(split.name); setSplitDays([...split.days]);
    const meso = !!split.isMesocycle; setIsMesocycle(meso);
    if (meso && split.weeks && split.weeks.length === 4) setMesocycleWeeks(split.weeks);
    else setMesocycleWeeks(makeDefaultMesocycleWeeks());
    setExpandedWeeks(new Set([1]));
  };

  const handleSaveSplit = async () => {
    if (!splitName.trim()) { toast.error('Please enter a split name'); return; }
    if (!editingSplit) return;
    const legacyDays = isMesocycle ? mesocycleWeeks[0].days : splitDays;
    const saved: WeeklySplit = { ...editingSplit, name: splitName.trim(), days: legacyDays, isMesocycle, weeks: isMesocycle ? mesocycleWeeks : undefined };
    await saveSplitMutation.mutateAsync(saved);
    toast.success(`Split "${saved.name}" saved!`);
    setEditingSplit(null);
  };

  const handleMesocycleDrop = (fromWeek: number, fromDay: number, toWeek: number, toDay: number) => {
    if (fromWeek === toWeek && fromDay === toDay) return;
    setMesocycleWeeks((prev) => {
      const next = prev.map((w) => ({ ...w, days: w.days.map((d) => ({ ...d })) }));
      const srcWeek = next.find((w) => w.weekNumber === fromWeek)!;
      const dstWeek = next.find((w) => w.weekNumber === toWeek)!;
      const srcDay = srcWeek.days.find((d) => d.dayIdx === fromDay)!;
      const dstDay = dstWeek.days.find((d) => d.dayIdx === toDay)!;
      const srcCat = srcDay.category;
      srcDay.category = dstDay.category;
      dstDay.category = srcCat;
      return next;
    });
    toast.success('Day categories swapped');
  };

  const handleDeleteSplit = async (id: string, name: string) => {
    if (id === 'ppl-default') { toast.error('Cannot delete the default PPL split'); return; }
    if (confirm(`Are you sure you want to delete "${name}"?`)) { await deleteSplitMutation.mutateAsync({ splitId: id }); toast.success(`Deleted split "${name}"`); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button variant="outline" size="sm" className="font-mono text-xs border-primary/40 text-primary hover:bg-primary/5 flex items-center gap-1.5">
            <Settings className="h-3.5 w-3.5" /> ADJUST SPLIT
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg bg-card border border-border text-foreground font-sans max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono uppercase text-sm tracking-wider text-primary flex items-center gap-1.5 border-b border-border/30 pb-2">
            <Calendar className="h-4 w-4" /> Split Command Center
          </DialogTitle>
        </DialogHeader>

        {!editingSplit ? (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest block">Active Training Split</label>

              {/* ── Fuel-style split list ── */}
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                {splits.map((s) => {
                  const accentColor = getSplitAccentColor(s.days);
                  return (
                    <div
                      key={s.id}
                      className={`flex items-center justify-between rounded-sm bg-card transition-all ${
                        s.isActive
                          ? 'ring-1 ring-primary/40'
                          : 'hover:bg-secondary/10'
                      }`}
                      style={{
                        borderLeft: `3px solid ${accentColor}`,
                        paddingLeft: '10px',
                        paddingRight: '8px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        borderTop: '1px solid hsl(var(--border) / 0.25)',
                        borderRight: '1px solid hsl(var(--border) / 0.25)',
                        borderBottom: '1px solid hsl(var(--border) / 0.25)',
                      }}
                    >
                      {/* Left content — clickable to activate */}
                      <div className="space-y-1.5 flex-1 min-w-0 cursor-pointer" onClick={() => handleSelectSplit(s.id)}>
                        {/* Row 1: name + badges */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm text-foreground leading-none">{s.name}</span>
                          {s.isActive && (
                            <span className="text-[8px] font-mono font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-sm uppercase tracking-wider flex items-center gap-0.5 shrink-0">
                              <Check className="h-2 w-2" /> ACTIVE
                            </span>
                          )}
                          {s.isMesocycle && (
                            <span className="text-[8px] font-mono font-bold bg-orange-500/20 text-orange-400 border border-orange-400/30 px-1.5 py-0.5 rounded-sm uppercase tracking-wider flex items-center gap-0.5 shrink-0">
                              <Layers className="h-2 w-2" /> 4-WEEK
                            </span>
                          )}
                        </div>

                        {/* Row 2: day breakdown */}
                        {s.isMesocycle && s.weeks ? (
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                            {s.weeks.map((w) => {
                              const style = getIntentStyle(w.intent);
                              return (
                                <span key={w.weekNumber} className={`text-[9px] font-mono px-1 py-0.5 rounded-sm border ${style.color}`}>
                                  W{w.weekNumber}: {style.label}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-x-2 gap-y-1 items-center">
                            {s.days.map((d) => (
                              <span key={d.dayIdx} className="flex items-center gap-1">
                                <span className="text-[9px] font-mono text-muted-foreground">{d.name.slice(0, 3)}:</span>
                                <SplitPill category={d.category} />
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Right: action icons */}
                      <div className="flex items-center gap-1 ml-3 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => handleEditSplit(s)} className="h-7 w-7 text-muted-foreground hover:text-primary">
                          <Settings className="h-3.5 w-3.5" />
                        </Button>
                        {s.id !== 'ppl-default' && (
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteSplit(s.id, s.name)} className="h-7 w-7 text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Button
              onClick={handleCreateNewSplit}
              variant="ghost"
              className="w-full bg-white border-[1.5px] border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5 font-mono text-xs flex items-center justify-center gap-1.5 rounded-lg"
            >
              <Plus className="h-4 w-4" /> CREATE NEW SPLIT
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest block">Split Name</label>
              <Input value={splitName} onChange={(e) => setSplitName(e.target.value)} placeholder="e.g. Arnold Split, Upper/Lower" className="bg-secondary border-border font-mono text-xs" />
            </div>

            {/* Mesocycle toggle */}
            <div className="flex items-center justify-between p-3 bg-secondary/20 border border-border/40 rounded-sm">
              <div>
                <p className="font-mono text-xs font-bold text-foreground flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-orange-400" /> 4-Week Mesocycle Mode</p>
                <p className="font-mono text-[10px] text-muted-foreground mt-0.5">{isMesocycle ? 'Configure 4 weeks with progressive intent' : 'Single repeating weekly pattern'}</p>
              </div>
              <button onClick={() => setIsMesocycle((v) => !v)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isMesocycle ? 'bg-orange-500' : 'bg-muted'}`}>
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isMesocycle ? 'translate-x-4' : 'translate-x-1'}`} />
              </button>
            </div>

            {isMesocycle ? (
              <div className="space-y-2">
                <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest block">Configure 4-Week Mesocycle</label>
                <p className="text-[10px] font-mono text-muted-foreground/70">Weeks 2-4 are collapsed by default. Tap a header to expand.</p>
                <div className="space-y-2">
                  {mesocycleWeeks.map((week) => (
                    <MesocycleWeekEditor
                      key={week.weekNumber} week={week}
                      isExpanded={expandedWeeks.has(week.weekNumber)}
                      onToggle={() => setExpandedWeeks((prev) => { const next = new Set(prev); if (next.has(week.weekNumber)) next.delete(week.weekNumber); else next.add(week.weekNumber); return next; })}
                      onIntentChange={(intent) => setMesocycleWeeks((prev) => prev.map((w) => w.weekNumber === week.weekNumber ? { ...w, intent } : w))}
                      onDayCategoryChange={(dayIdx, category) => setMesocycleWeeks((prev) => prev.map((w) => w.weekNumber === week.weekNumber ? { ...w, days: w.days.map((d) => d.dayIdx === dayIdx ? { ...d, category } : d) } : w))}
                      onDrop={handleMesocycleDrop}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest block">Configure 7-Day Target Alignment</label>
                <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
                  {splitDays.map((day) => {
                    const isPreset = CATEGORIES.filter((c) => c !== 'Custom...').includes(day.category);
                    return (
                      <div key={day.dayIdx} className="flex flex-col gap-1.5 p-2.5 bg-secondary/10 border border-border/40 rounded-sm">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-xs font-bold text-foreground shrink-0">{day.name}</span>
                            {day.category && <SplitPill category={day.category} />}
                          </div>
                          <Select value={isPreset ? day.category : 'Custom...'} onValueChange={(val) => { if (val === 'Custom...') { setSplitDays((prev) => prev.map((d) => d.dayIdx === day.dayIdx ? { ...d, category: '' } : d)); } else { setSplitDays((prev) => prev.map((d) => d.dayIdx === day.dayIdx ? { ...d, category: val } : d)); } }}>
                            <SelectTrigger className="w-32 bg-secondary border-border font-mono text-xs h-8 shrink-0"><SelectValue placeholder="Select target" /></SelectTrigger>
                            <SelectContent className="bg-card border border-border font-mono text-xs">
                              {CATEGORIES.map((cat) => <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        {(!isPreset || day.category === '') && (
                          <Input value={day.category} onChange={(e) => setSplitDays((prev) => prev.map((d) => d.dayIdx === day.dayIdx ? { ...d, category: e.target.value } : d))} placeholder="Type custom label (e.g. Heavy Bench)" className="bg-background border-border font-mono text-xs h-8" maxLength={15} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-2 border-t border-border/30 pt-3">
              <Button onClick={handleSaveSplit} disabled={saveSplitMutation.isPending} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs">SAVE SPLIT</Button>
              <Button variant="outline" onClick={() => setEditingSplit(null)} className="flex-1 font-mono text-xs">CANCEL</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
